import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Edit2, Trash2, ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, MoreVertical, Share2, Calendar, Flag, Sparkles, Send } from 'lucide-react';
import { formatDate } from '../utils/mockData';
import '../styles/CandidateTableCustom.css';
import { formatFullName } from '../utils/nameFormatter';

/* ── Helper: initials from name ── */
const getInitials = (first, last) => {
  const f = (first || '').charAt(0).toUpperCase();
  const l = (last || '').charAt(0).toUpperCase();
  return f + l || '?';
};

/* ── Avatar colour from name (deterministic) ── */
const AVATAR_COLORS = [
  '#3b5bfd', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
  '#2563eb', '#6366f1', '#ef4444', '#1d4ed8', '#f97316',
];
const avatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/* ── Normalize skills: split "Html,css,javascript" into ["Html","css","javascript"] ── */
const normalizeSkills = (skills = []) =>
  skills.flatMap(s => {
    const name = (s.SkillName || s || '').toString().trim();
    return name.includes(',')
      ? name.split(',').map(n => n.trim()).filter(Boolean)
      : name ? [name] : [];
  });

/* ── Format phone: ensure '+' prefix for international numbers ── */
const formatPhone = (phone) => {
  if (!phone) return null;
  const cleaned = phone.toString().trim();
  if (!cleaned) return null;
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
};

/* ── Sort Icon ── */
const SortIcon = ({ columnKey, sortConfig }) => {
  if (sortConfig.key !== columnKey)
    return <ArrowUpDown size={12} className="ct-sort-icon ct-sort-icon--inactive" />;
  return sortConfig.direction === 'asc'
    ? <ArrowUp size={12} className="ct-sort-icon ct-sort-icon--active" />
    : <ArrowDown size={12} className="ct-sort-icon ct-sort-icon--active" />;
};

/* ── Status Badge ── */
const StatusBadge = ({ status }) => {
  const map = {
    Available: 'ct-badge--green',
    Hired: 'ct-badge--blue',
    'In Process': 'ct-badge--yellow',
    Interviewing: 'ct-badge--yellow',
    'Not Available': 'ct-badge--red',
    Unavailable: 'ct-badge--red',
    Placed: 'ct-badge--purple',
    'On Hold': 'ct-badge--gray',
    'On Bench': 'ct-badge--orange',
  };

  return <span className={`ct-badge ${map[status] || 'ct-badge--gray'}`}>{status || '—'}</span>;
};

/* ── Remote Badge ── */
const RemoteBadge = ({ status }) => {
  const map = {
    Remote: 'ct-badge--teal',
    OnSite: 'ct-badge--orange',
    Hybrid: 'ct-badge--indigo',
  };
  return (
    <span className={`ct-badge ${map[status] || 'ct-badge--gray'}`}>
      {status === 'OnSite' ? 'On-Site' : status || '—'}
    </span>
  );
};

/* ──────────────────────────────────────────────────────────────
   Main Component
   ────────────────────────────────────────────────────────────── */
const CandidateTable = ({
  candidates = [],
  loading = false,
  onView,
  onEdit,
  onShare,
  onSchedule,
  onDelete,
  visibleColumns = [],
  sortConfig = { key: '', direction: 'asc' },
  onSort,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  onPageChange,
  itemsPerPage = 30,
  onItemsPerPageChange,
  selectedRows = [],
  isAiSearching = false,
  onSelectionChange,
  onSubmitProfile,
}) => {
  const navigate = useNavigate();
  const show = (key) => visibleColumns.includes(key);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredSkills, setHoveredSkills] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, position: 'top', maxHeight: null });
  const [actionDropdown, setActionDropdown] = useState(null);
  const skillsRefMap = useRef({});
  const closeTimeoutRef = useRef(null);
  const tableWrapperRef = useRef(null);

  // Keyboard Right / Left arrow horizontal scrolling
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't scroll if user is typing in an input, textarea, select, or editable element
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isInputActive = activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select' || document.activeElement?.isContentEditable;
      if (isInputActive) return;

      if (tableWrapperRef.current) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          tableWrapperRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          tableWrapperRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.ct-action-dropdown-wrapper')) {
        setActionDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSkillsEnter = useCallback((candidateId, el) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    if (el) {
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceAbove = rect.top;
      const spaceBelow = windowHeight - rect.bottom;

      // Show below if there's very little space above OR there's a lot more space below
      const showBelow = spaceAbove < 250 || spaceBelow > (spaceAbove + 100);

      setTooltipPos({
        top: showBelow ? rect.bottom + 8 : rect.top - 8,
        left: Math.min(rect.left, window.innerWidth - 380), // Prevent going off right edge (360px max-width + padding)
        position: showBelow ? 'bottom' : 'top',
        maxHeight: showBelow ? Math.max(150, spaceBelow - 24) : Math.max(150, spaceAbove - 24)
      });
    }
    setHoveredSkills(candidateId);
  }, []);

  const handleSkillsLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredSkills(null);
    }, 250);
  }, []);

  const SortTh = ({ label, columnKey, className = '' }) => (
    <th
      className={`ct-th ct-th--sortable ${className}`}
      onClick={() => onSort && onSort(columnKey)}
    >
      <span className="ct-th-inner">
        {label}
        <SortIcon columnKey={columnKey} sortConfig={sortConfig} />
      </span>
    </th>
  );

  if (isAiSearching) {
    return (
      <div className="ct-empty ct-ai-searching-container">
        <div className="ct-ai-pulse-wrapper">
          <Sparkles className="ct-ai-pulse-icon" size={48} color="#3b5bfd" fill="#3b5bfd" />
          <div className="ct-ai-pulse-ring"></div>
          <div className="ct-ai-pulse-ring" style={{ animationDelay: '1s' }}></div>
          <div className="ct-ai-pulse-ring" style={{ animationDelay: '2s' }}></div>
        </div>
        <p className="ct-empty-title ct-ai-searching-text">AI is analyzing your database...</p>
        <p className="ct-empty-sub">Finding the best matches based on semantic relevance</p>

        <div className="ct-ai-progress-bar">
          <div className="ct-ai-progress-inner"></div>
        </div>
      </div>
    );
  }

  if (loading) {
    const skeletonRows = Array.from({ length: 10 });
    return (
      <div className="ct-container">
        {/* Preloader Banner */}
        <div className="cm-preloader-banner">
          <div className="cm-loading-spinner" style={{ width: '18px', height: '18px', borderWidth: '2.5px' }}></div>
          <span className="cm-preloader-text">Loading candidate records...</span>
        </div>

        <div className="ct-wrapper" style={{ overflow: 'hidden' }}>
          <table className="ct-table">
            <thead>
              <tr>
                <th className="ct-th" style={{ width: '1%', minWidth: '30px', padding: '0 8px', textAlign: 'center' }}>
                  <input type="checkbox" disabled style={{ accentColor: '#3b5bfd', width: 15, height: 15 }} />
                </th>
                {show('CandidateCode') && <th className="ct-th">Candidate ID</th>}
                {show('Candidate') && <th className="ct-th">Name</th>}
                {show('EmployeeCode') && <th className="ct-th">Employee ID</th>}
                {show('Email') && <th className="ct-th">Email</th>}
                {show('Phone') && <th className="ct-th">Mobile</th>}
                {show('JobTitle') && <th className="ct-th">Job Title</th>}
                {show('SubmittedBy') && <th className="ct-th">Created By</th>}
                {show('YearsOfExperience') && <th className="ct-th ct-th--center">Exp.</th>}
                {show('Skills') && <th className="ct-th">Skills</th>}
                {show('CandidateStatus') && <th className="ct-th">Status</th>}
                {show('RemoteStatus') && <th className="ct-th">Work Type</th>}
                {show('WorkAuthorization') && <th className="ct-th">Work Auth</th>}
                {show('BenchPriority') && <th className="ct-th">Bench Priority</th>}
                {show('DaysOnBench') && <th className="ct-th">Days on Bench</th>}
                {show('BenchAssignee') && <th className="ct-th">Bench Recruiter</th>}
                {show('CurrentLocation') && <th className="ct-th">Location</th>}
                {show('Actions') && <th className="ct-th ct-th--center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {skeletonRows.map((_, idx) => (
                <tr key={idx} className="cm-skeleton-row">
                  <td className="ct-td" style={{ textAlign: 'center' }}>
                    <div className="cm-skeleton-cell" style={{ width: '15px', height: '15px', margin: '0 auto', borderRadius: '3px' }}></div>
                  </td>
                  {show('CandidateCode') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '85px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('Candidate') && (
                    <td className="ct-td">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="cm-skeleton-cell" style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0 }}></div>
                        <div className="cm-skeleton-cell" style={{ width: '120px', height: '14px', borderRadius: '4px' }}></div>
                      </div>
                    </td>
                  )}
                  {show('EmployeeCode') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '60px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('Email') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '160px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('Phone') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '100px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('JobTitle') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '140px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('SubmittedBy') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '90px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('YearsOfExperience') && (
                    <td className="ct-td" style={{ textAlign: 'center' }}>
                      <div className="cm-skeleton-cell" style={{ width: '30px', height: '14px', margin: '0 auto', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('Skills') && (
                    <td className="ct-td">
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <div className="cm-skeleton-cell" style={{ width: '48px', height: '20px', borderRadius: '10px' }}></div>
                        <div className="cm-skeleton-cell" style={{ width: '42px', height: '20px', borderRadius: '10px' }}></div>
                      </div>
                    </td>
                  )}
                  {show('CandidateStatus') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '70px', height: '20px', borderRadius: '10px' }}></div>
                    </td>
                  )}
                  {show('RemoteStatus') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '60px', height: '20px', borderRadius: '10px' }}></div>
                    </td>
                  )}
                  {show('WorkAuthorization') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '55px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('BenchPriority') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '40px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('DaysOnBench') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '35px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('BenchAssignee') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '85px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('CurrentLocation') && (
                    <td className="ct-td">
                      <div className="cm-skeleton-cell" style={{ width: '90px', height: '14px', borderRadius: '4px' }}></div>
                    </td>
                  )}
                  {show('Actions') && (
                    <td className="ct-td" style={{ textAlign: 'center' }}>
                      <div className="cm-skeleton-cell" style={{ width: '24px', height: '24px', margin: '0 auto', borderRadius: '4px' }}></div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!candidates || candidates.length === 0) {
    return (
      <div className="ct-empty">
        <div className="ct-empty-icon text-gray-300">📋</div>
        <p className="ct-empty-title">No candidates found</p>
        <p className="ct-empty-sub">Try adjusting your search or filters</p>
      </div>
    );
  }

  return (
    <div className="ct-container">
      <div className="ct-wrapper" ref={tableWrapperRef} tabIndex={0} style={{ outline: 'none' }}>
        <table className="ct-table">
          <thead>
            <tr>
              <th className="ct-th" style={{ width: '1%', minWidth: '30px', padding: '0 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={candidates.length > 0 && selectedRows && candidates.every(c => selectedRows.includes(c.CandidateId))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newSelected = [...(selectedRows || [])];
                      candidates.forEach(c => {
                        if (!newSelected.includes(c.CandidateId)) newSelected.push(c.CandidateId);
                      });
                      onSelectionChange && onSelectionChange(newSelected);
                    } else {
                      const currentIds = candidates.map(c => c.CandidateId);
                      onSelectionChange && onSelectionChange((selectedRows || []).filter(id => !currentIds.includes(id)));
                    }
                  }}
                  style={{ accentColor: '#3b5bfd', width: 15, height: 15, cursor: 'pointer' }}
                />
              </th>
              {show('CandidateCode') && <SortTh label="Candidate ID" columnKey="CandidateCode" />}
              {show('Candidate') && <SortTh label="Name" columnKey="FirstName" />}
              {show('EmployeeCode') && <SortTh label="Employee ID" columnKey="EmployeeCode" />}
              {show('Email') && <SortTh label="Email" columnKey="Email" />}
              {show('Phone') && <th className="ct-th">Mobile</th>}
              {show('JobTitle') && <SortTh label="Job Title" columnKey="JobTitle" />}
              {show('SubmittedBy') && <SortTh label="Created By" columnKey="SubmittedByRecruiter" />}
              {show('YearsOfExperience') && <SortTh label="Exp." columnKey="YearsOfExperience" className="ct-th--center" />}
              {show('Skills') && <th className="ct-th">Skills</th>}
              {show('CandidateStatus') && <SortTh label="Status" columnKey="CandidateStatus" />}
              {show('RemoteStatus') && <th className="ct-th">Work Type</th>}
              {show('WorkAuthorization') && <SortTh label="Work Auth" columnKey="WorkAuthorization" />}
              {show('BenchPriority') && <SortTh label="Bench Priority" columnKey="BenchPriority" />}
              {show('DaysOnBench') && <SortTh label="Days on Bench" columnKey="DaysOnBench" />}
              {show('BenchAssignee') && <SortTh label="Bench Recruiter" columnKey="BenchAssignee" />}
              {show('WorkAuthPetitioner') && <SortTh label="Petitioner" columnKey="WorkAuthPetitioner" />}
              {show('WorkAuthExpiryDate') && <SortTh label="Visa Expiry" columnKey="WorkAuthExpiryDate" />}
              {show('NewJobDeadline') && <SortTh label="Placement Deadline" columnKey="NewJobDeadline" />}
              {show('OutOfJobSince') && <SortTh label="Out of Job Since" columnKey="OutOfJobSince" />}
              {show('EmploymentType') && <SortTh label="Emp Type" columnKey="EmploymentType" />}
              {show('CreatedDate') && <SortTh label="Created" columnKey="CreatedDt" />}
              {show('LinkedInUrl') && <th className="ct-th">LinkedIn</th>}
              {show('GitHubUrl') && <th className="ct-th">GitHub</th>}
              {show('CurrentLocation') && <SortTh label="Location" columnKey="CurrentLocation" />}
              {show('Gender') && <SortTh label="Gender" columnKey="Gender" />}
              {show('PersonType') && <SortTh label="Type" columnKey="IsEmployee" />}
              {show('IsBench') && <SortTh label="Bench" columnKey="IsBench" />}
              {show('Experience') && <SortTh label="Experience" columnKey="YearsOfExperience" />}
              {show('Relocation') && <SortTh label="Reloc." columnKey="WillingToRelocate" />}
              {show('Source') && <th className="ct-th">Source</th>}
              {show('ProfileSummary') && <th className="ct-th">Summary</th>}
              {show('Actions') && <th className="ct-th ct-th--center">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {candidates.map((c, idx) => {
              const fullName = formatFullName(c.FirstName, c.MiddleName, c.LastName);
              const color = avatarColor(fullName || 'Unknown');
              const isHovered = hoveredRow === c.CandidateId;

              return (
                <tr
                  key={c.CandidateId}
                  className={`ct-row ${idx % 2 === 0 ? '' : 'ct-row--alt'} ${isHovered ? 'ct-row--hover' : ''}`}
                  onMouseEnter={() => setHoveredRow(c.CandidateId)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{ zIndex: actionDropdown === c.CandidateId ? 99 : 'auto', position: 'relative' }}
                >

                  <td className="ct-td" style={{ width: '1%', minWidth: '30px', padding: '0 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={selectedRows?.includes(c.CandidateId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onSelectionChange && onSelectionChange([...(selectedRows || []), c.CandidateId]);
                        } else {
                          onSelectionChange && onSelectionChange((selectedRows || []).filter(id => id !== c.CandidateId));
                        }
                      }}
                      style={{ accentColor: '#3b5bfd', width: 15, height: 15, cursor: 'pointer' }}
                    />
                  </td>

                  {show('CandidateCode') && (
                    <td className="ct-td">
                      <span
                        className="ct-code"
                        onClick={() => navigate(`/candidates/${c.CandidateId}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {c.CandidateCode || '—'}
                      </span>
                    </td>
                  )}

                  {show('Candidate') && (
                    <td className="ct-td">
                      <div className="ct-name-cell">
                        <div className="ct-avatar" style={{ background: color }}>
                          {getInitials(c.FirstName, c.LastName)}
                        </div>
                        <span
                          className="ct-name-link"
                          onClick={() => navigate(`/candidates/${c.CandidateId}`)}
                          title={fullName}
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          {fullName || '—'}
                          {c.IsBench && (
                            <span
                              title="Bench Candidate"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#fef3c7',
                                color: '#d97706',
                                borderRadius: '4px',
                                padding: '2px 4px'
                              }}
                            >
                              <Flag size={12} fill="#d97706" />
                            </span>
                          )}
                        </span>
                      </div>
                      {c.matchReason && (
                        <div className="ct-ai-reason" style={{
                          fontSize: '11.5px', color: '#3b5bfd', marginTop: '4px',
                          fontStyle: 'italic', display: 'flex', alignItems: 'flex-start', gap: '6px',
                          paddingLeft: '32px', lineHeight: '1.4', maxWidth: '300px'
                        }}>
                          <Sparkles size={12} style={{ marginTop: '2px', flexShrink: 0 }} />
                          <span>{c.matchReason}</span>
                        </div>
                      )}
                    </td>
                  )}

                  {show('EmployeeCode') && (
                    <td className="ct-td">
                      <span className="ct-code" style={{ color: '#64748b' }}>
                        {c.EmployeeCode || '—'}
                      </span>
                    </td>
                  )}

                  {show('Email') && (
                    <td className="ct-td">
                      <span className="ct-email" title={c.Contact?.Email || ''}>
                        {c.Contact?.Email || <span className="ct-na">—</span>}
                      </span>
                    </td>
                  )}

                  {show('Phone') && (
                    <td className="ct-td">
                      <span className="ct-phone">{formatPhone(c.Contact?.Phone || c.Mobile || c.Contact?.Mobile) || <span className="ct-na">—</span>}</span>
                    </td>
                  )}

                  {show('JobTitle') && (
                    <td className="ct-td">
                      <span
                        className="ct-jobtitle"
                        title={c.JobTitle || ''}
                        style={{
                          cursor: 'pointer',
                          maxWidth: '200px',
                          display: 'inline-block'
                        }}
                      >
                        {c.JobTitle ? (
                          c.JobTitle.length > 25
                            ? `${c.JobTitle.substring(0, 25)}...`
                            : c.JobTitle
                        ) : (
                          <span className="ct-na">—</span>
                        )}
                      </span>
                    </td>
                  )}

                  {show('SubmittedBy') && (
                    <td className="ct-td">
                      {c.SubmittedByRecruiter ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11.5px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                          title={`Created by: ${c.SubmittedByRecruiter}`}
                        >
                          👤 {c.SubmittedByRecruiter}
                        </span>
                      ) : (c.SourceType === 'career_portal' || (c.Source || '').toLowerCase().includes('career') || (c.Source || '').toLowerCase().includes('ats') || (c.Source || '').toLowerCase().includes('portal')) ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11.5px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                          title="Applied via Careers Portal (ATS)"
                        >
                          🌐 Career Portal
                        </span>
                      ) : (
                        <span className="ct-na">—</span>
                      )}
                    </td>
                  )}

                  {show('YearsOfExperience') && (
                    <td className="ct-td ct-td--center">
                      {c.YearsOfExperience != null
                        ? <span className="ct-exp">{c.YearsOfExperience}<small> yr</small></span>
                        : <span className="ct-na">—</span>}
                    </td>
                  )}

                  {show('Skills') && (() => {
                    const skills = normalizeSkills(c.Skills);
                    return (
                      <td className="ct-td ct-td--skills">
                        <div
                          className="ct-skills-wrapper"
                          ref={el => { if (el) skillsRefMap.current[c.CandidateId] = el; }}
                          onMouseEnter={(e) => skills.length > 0 && handleSkillsEnter(c.CandidateId, e.currentTarget)}
                          onMouseLeave={handleSkillsLeave}
                        >
                          <div className="ct-skills">
                            {skills.length > 0 ? (
                              <>
                                {skills.slice(0, 2).map((name, i) => (
                                  <span key={i} className="ct-skill-chip">{name}</span>
                                ))}
                                {skills.length > 2 && (
                                  <span style={{ color: '#6b7280', fontWeight: 'bold', marginLeft: '4px', fontSize: '14px', letterSpacing: '1px' }}>...</span>
                                )}
                              </>
                            ) : (
                              <span className="ct-na">—</span>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })()}

                  {show('CandidateStatus') && (
                    <td className="ct-td ct-td--no-clip">
                      <StatusBadge status={c.CandidateStatus} />
                    </td>
                  )}

                  {show('RemoteStatus') && (
                    <td className="ct-td ct-td--no-clip">
                      <RemoteBadge status={c.RemoteStatus} />
                    </td>
                  )}

                  {show('WorkAuthorization') && (
                    <td className="ct-td">
                      <span className="ct-badge ct-badge--yellow" style={{ fontSize: '11px', fontWeight: 600 }}>
                        {c.WorkAuthorization || '—'}
                      </span>
                    </td>
                  )}

                  {show('BenchPriority') && (
                    <td className="ct-td">
                      {c.BenchPriority ? (
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                          background: (c.BenchPriority || '').toUpperCase() === 'CRITICAL' ? '#fee2e2' : (c.BenchPriority || '').toUpperCase() === 'HIGH' ? '#fee2e2' : (c.BenchPriority || '').toUpperCase() === 'MEDIUM' ? '#fef3c7' : '#d1fae5',
                          color: (c.BenchPriority || '').toUpperCase() === 'CRITICAL' ? '#991b1b' : (c.BenchPriority || '').toUpperCase() === 'HIGH' ? '#b91c1c' : (c.BenchPriority || '').toUpperCase() === 'MEDIUM' ? '#92400e' : '#065f46'
                        }}>
                          {(c.BenchPriority || '').toUpperCase() === 'CRITICAL' ? '🔥 Critical' : (c.BenchPriority || '').toUpperCase() === 'HIGH' ? '🔴 High' : (c.BenchPriority || '').toUpperCase() === 'MEDIUM' ? '🟡 Medium' : '🟢 Low'}
                        </span>
                      ) : <span className="ct-na">—</span>}
                    </td>
                  )}

                  {show('DaysOnBench') && (
                    <td className="ct-td">
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                        background: (c.DaysOnBench >= 46 ? '#fee2e2' : c.DaysOnBench >= 31 ? '#ffedd5' : c.DaysOnBench >= 16 ? '#fef3c7' : '#d1fae5'),
                        color: (c.DaysOnBench >= 46 ? '#991b1b' : c.DaysOnBench >= 31 ? '#c2410c' : c.DaysOnBench >= 16 ? '#92400e' : '#065f46'),
                        display: 'inline-flex', alignItems: 'center', gap: '3px'
                      }}>
                        {c.DaysOnBench || 0} days
                      </span>
                    </td>
                  )}

                  {show('BenchAssignee') && (
                    <td className="ct-td">
                      {(() => {
                        const list = (c.BenchAssignees && c.BenchAssignees.length > 0)
                          ? c.BenchAssignees
                          : (c.BenchAssignee ? [c.BenchAssignee] : []);
                        if (list.length === 0) return <span className="ct-na">—</span>;

                        const primary = list.find(r => typeof r === 'object' && (r.IsPrimary === 1 || r.IsPrimary === true)) || list[0];
                        const primaryName = typeof primary === 'string' ? primary : (primary.UserName || primary.name || '');
                        const remaining = list.filter(r => (typeof r === 'string' ? r : (r.UserName || r.name || '')) !== primaryName);
                        const displayName = primaryName.length > 15 ? `${primaryName.substring(0, 15)}...` : primaryName;
                        const allNames = list.map(r => typeof r === 'string' ? r : (r.UserName || r.name || '')).filter(Boolean).join(', ');

                        return (
                          <div
                            title={allNames}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', cursor: 'default' }}
                          >
                            <span
                              title={allNames}
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                background: '#ecfdf5',
                                color: '#065f46',
                                border: '1px solid #a7f3d0',
                                padding: '2px 7px',
                                borderRadius: '8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                cursor: 'default',
                                lineHeight: 1.3
                              }}
                            >
                              <span style={{ fontSize: '10px' }}>⭐</span>
                              <span>{displayName}</span>
                            </span>

                            {remaining.length > 0 && (
                              <span
                                title={allNames}
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  background: '#f1f5f9',
                                  color: '#475569',
                                  border: '1px solid #cbd5e1',
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  lineHeight: 1.3
                                }}
                              >
                                +{remaining.length}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  )}

                  {show('WorkAuthPetitioner') && (
                    <td className="ct-td">
                      <span style={{ fontSize: '12px', color: '#334155' }}>{c.WorkAuthPetitioner || <span className="ct-na">—</span>}</span>
                    </td>
                  )}

                  {show('WorkAuthExpiryDate') && (
                    <td className="ct-td">
                      <span style={{ fontSize: '12px', color: '#334155' }}>{formatDate(c.WorkAuthExpiryDate) || <span className="ct-na">—</span>}</span>
                    </td>
                  )}

                  {show('NewJobDeadline') && (
                    <td className="ct-td">
                      <span style={{ fontSize: '12px', color: '#334155' }}>{formatDate(c.NewJobDeadline) || <span className="ct-na">—</span>}</span>
                    </td>
                  )}

                  {show('OutOfJobSince') && (
                    <td className="ct-td">
                      <span style={{ fontSize: '12px', color: '#334155' }}>{formatDate(c.OutOfJobSince) || <span className="ct-na">—</span>}</span>
                    </td>
                  )}

                  {show('EmploymentType') && (
                    <td className="ct-td">
                      <span className="ct-badge ct-badge--teal" style={{ fontSize: '11px' }}>{c.EmploymentType || '—'}</span>
                    </td>
                  )}

                  {show('CreatedDate') && (
                    <td className="ct-td ct-td--no-clip">
                      <span className="ct-date">{formatDate(c.CreatedDt) || '—'}</span>
                    </td>
                  )}

                  {show('LinkedInUrl') && (
                    <td className="ct-td">
                      {c.LinkedInUrl
                        ? <a href={/^https?:\/\//i.test(c.LinkedInUrl.trim()) ? c.LinkedInUrl.trim() : `https://${c.LinkedInUrl.trim()}`} target="_blank" rel="noopener noreferrer" className="ct-link" title={c.LinkedInUrl} style={{ color: '#0077b5', textDecoration: 'none', fontSize: '12px' }}>
                          🔗 LinkedIn
                        </a>
                        : <span className="ct-na">—</span>}
                    </td>
                  )}

                  {show('GitHubUrl') && (
                    <td className="ct-td">
                      {c.GitHubUrl
                        ? <a href={/^https?:\/\//i.test(c.GitHubUrl.trim()) ? c.GitHubUrl.trim() : `https://${c.GitHubUrl.trim()}`} target="_blank" rel="noopener noreferrer" className="ct-link" title={c.GitHubUrl} style={{ color: '#333', textDecoration: 'none', fontSize: '12px' }}>
                          🐙 GitHub
                        </a>
                        : <span className="ct-na">—</span>}
                    </td>
                  )}

                  {show('CurrentLocation') && (
                    <td className="ct-td">
                      <span className="ct-jobtitle" title={c.CurrentLocation || ''}>
                        {c.CurrentLocation || <span className="ct-na">—</span>}
                      </span>
                    </td>
                  )}

                  {show('Gender') && (
                    <td className="ct-td">
                      <span>{c.Gender || <span className="ct-na">—</span>}</span>
                    </td>
                  )}

                  {show('PersonType') && (
                    <td className="ct-td">
                      <span className={`ct-badge ${c.IsEmployee ? 'ct-badge--indigo' : 'ct-badge--teal'}`}>
                        {c.IsEmployee ? 'Employee' : 'Candidate'}
                      </span>
                    </td>
                  )}

                  {show('IsBench') && (
                    <td className="ct-td">
                      <span className={`ct-badge ${c.IsBench ? 'ct-badge--orange' : 'ct-badge--gray'}`}>
                        {c.IsBench ? 'Bench' : 'Non-Bench'}
                      </span>
                    </td>
                  )}

                  {show('Experience') && (
                    <td className="ct-td">
                      <span>{c.YearsOfExperience != null ? `${c.YearsOfExperience} yr` : <span className="ct-na">—</span>}</span>
                    </td>
                  )}

                  {show('Relocation') && (
                    <td className="ct-td">
                      <span className={`ct-badge ${c.WillingToRelocate ? 'ct-badge--green' : 'ct-badge--red'}`}>
                        {c.WillingToRelocate ? 'Yes' : 'No'}
                      </span>
                    </td>
                  )}

                  {show('Source') && (
                    <td className="ct-td">
                      {c.SourceType === 'career_portal' || (c.Source || '').toLowerCase().includes('career') || (c.Source || '').toLowerCase().includes('ats') || (c.Source || '').toLowerCase().includes('portal') ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11.5px',
                          fontWeight: 500,
                          whiteSpace: 'nowrap'
                        }}>
                          🌐 Career Portal
                        </span>
                      ) : (
                        <span>{c.Source || 'Recruiter'}</span>
                      )}
                    </td>
                  )}

                  {show('ProfileSummary') && (
                    <td className="ct-td" style={{ maxWidth: '200px' }}>
                      <span
                        className="ct-jobtitle"
                        title={c.ProfileSummary || ''}
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px', color: '#6b7280' }}
                      >
                        {c.ProfileSummary || <span className="ct-na">—</span>}
                      </span>
                    </td>
                  )}

                  {show('Actions') && (
                    <td className="ct-td ct-td--center" style={{ overflow: 'visible', position: 'relative' }}>
                      <div className={`ct-actions ${(isHovered || actionDropdown === c.CandidateId) ? 'ct-actions--visible' : ''}`}>
                        <div className="ct-action-dropdown-wrapper" style={{ position: 'relative' }}>
                          <button
                            className="ct-action-btn ct-action-btn--view"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionDropdown(actionDropdown === c.CandidateId ? null : c.CandidateId);
                            }}
                            title="More Actions"
                          >
                            <MoreVertical size={15} />
                          </button>

                          {actionDropdown === c.CandidateId && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              marginTop: '4px',
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                              borderRadius: '8px',
                              padding: '6px',
                              zIndex: 50,
                              minWidth: '170px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px'
                            }}>
                              <button
                                className="ct-dropdown-item"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', borderRadius: '6px', textAlign: 'left', width: '100%' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActionDropdown(null);
                                  if (onShare) onShare(c);
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Share2 size={14} color="#6b7280" /> Share Resume
                              </button>
                              <button
                                className="ct-dropdown-item"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', borderRadius: '6px', textAlign: 'left', width: '100%' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActionDropdown(null);
                                  if (onSchedule) onSchedule(c);
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Calendar size={14} color="#6b7280" /> Schedule Interview
                              </button>
                              <button
                                className="ct-dropdown-item"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', borderRadius: '6px', textAlign: 'left', width: '100%' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActionDropdown(null);
                                  if (onSubmitProfile) onSubmitProfile(c);
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Send size={14} color="#6b7280" /> Submit Profile
                              </button>
                            </div>
                          )}
                        </div>

                        <button
                          className="ct-action-btn ct-action-btn--edit"
                          onClick={() => onEdit && onEdit(c)}
                          title="Edit"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          className="ct-action-btn ct-action-btn--delete"
                          onClick={() => onDelete && onDelete(c.CandidateId)}
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  )}

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="ct-pagination">
          <span className="ct-pagination-info">
            Showing <strong>{(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, totalCount)}</strong> of <strong>{totalCount}</strong>

            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(Number(e.target.value))}
              style={{
                marginLeft: '12px',
                padding: '4px 8px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                outline: 'none',
                background: '#fff',
                color: '#374151',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              {[10, 15, 20, 25, 30, 35, 40, 45, 50].map(val => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </span>
          <div className="ct-pagination-btns">
            <button
              className="ct-page-btn"
              disabled={currentPage <= 1}
              onClick={() => onPageChange && onPageChange(currentPage - 1)}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let page;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <button
                  key={page}
                  className={`ct-page-btn ct-page-num ${currentPage === page ? 'ct-page-num--active' : ''}`}
                  onClick={() => onPageChange && onPageChange(page)}
                >
                  {page}
                </button>
              );
            })}
            <button
              className="ct-page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange && onPageChange(currentPage + 1)}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── Skills Tooltip Portal (renders outside the table to avoid overflow clipping) ── */}
      {hoveredSkills != null && (() => {
        const c = candidates.find(cd => cd.CandidateId === hoveredSkills);
        if (!c) return null;
        const skills = normalizeSkills(c.Skills);
        if (skills.length === 0) return null;
        return createPortal(
          <div
            className="ct-skills-tooltip"
            onMouseEnter={() => {
              if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
            }}
            onMouseLeave={handleSkillsLeave}
            style={{
              position: 'fixed',
              top: tooltipPos.top,
              left: tooltipPos.left,
              transform: tooltipPos.position === 'top' ? 'translateY(-100%)' : 'none',
              zIndex: 99999,
              pointerEvents: 'auto',
              maxHeight: tooltipPos.maxHeight ? `${tooltipPos.maxHeight}px` : undefined,
              overflowY: 'auto'
            }}
          >
            <div className="ct-tooltip-header">
              <span className="ct-tooltip-title">All Skills</span>
              <span className="ct-tooltip-count">{skills.length}</span>
            </div>
            <div className="ct-tooltip-chips">
              {skills.map((name, i) => (
                <span key={i} className="ct-tooltip-chip">{name}</span>
              ))}
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default CandidateTable;